import type { H3Event } from "h3";
import { getDatoLocaleFromCulture } from "~~/server/utils/mapping/localeMapping";
import { getSlugPrefix } from "~~/server/utils/slugPrefixes";

export default defineAppConfig({
  ui: {
    colors: {
      primary: "main",
      neutral: "neutral",
      blue: "blue",
      bakery: "bakery",
      dairy: "dairy",
      drinks: "drinks",
      dry: "dry",
      fruit: "fruit",
      meat: "meat",
      non: "non",
      sauces: "sauces",
      snacks: "snacks",
    },
    accordion: {
      slots: {
        header: "bg-primary-100 text-primary font-bold rounded-full px-sm md:px-md mb-md",
      },
    },
    badge: {
      slots: {
        base: "font-semibold rounded-2xl",
      },
    },
    button: {
      slots: {
        base: "flex flex-wrap justify-center transition rounded-full px-sm md:px-md py-xs md:py-sm leading-6 cursor-pointer disabled:cursor-not-allowed",
      },
      compoundVariants: [
        {
          color: "neutral",
          variant: "link",
          class: "text-default hover:text-default/75 active:text-default/75 disabled:text-default aria-disabled:text-default",
        },
      ],
    },
    fieldGroup: {
      variants: {
        orientation: {
          horizontal: "w-full",
        },
      },
    },
    checkbox: {
      slots: {
        wrapper: "relative flex items-center",
      },
    },
    dropdownMenu: {
      slots: {
        content: "ring-1 ring-neutral-100 shadow-none",
        item: "cursor-pointer select-none relative flex items-center justify-between gap-1 px-1 py-1 hover:bg-[#F3F1EC]",
      },
    },
    formField: {
      slots: {
        label: "font-semibold",
      },
    },
    input: {
      slots: {
        root: "w-full",
        base: "border border-0.5 border-neutral-100 font-medium placeholder:text-[#00103599] bg-white focus:ring-1! focus:ring-non! focus-visible:ring-non!",
      },
    },
    inputMenu: {
      slots: {
        base: "focus:ring-1! focus:ring-non! focus-visible:ring-non!",
      },
    },
    pagination: {
      slots: {
        item: "border-0 ring-0 shadow-none size-10 flex justify-center rounded-md",
        first: "hidden!",
        last: "hidden!",
      },
    },
    radioGroup: {
      slots: {
        item: "items-start",
      },
    },
    select: {
      slots: {
        base: "w-full border border-0.5 border-neutral-100 bg-white ring-0 focus:ring-1! focus:ring-non! focus-visible:ring-non!",
      },
    },
    selectMenu: {
      slots: {
        base: "w-full",
        content: "ring-1 ring-neutral-100 shadow-none",
        item: "cursor-pointer select-none relative flex items-center justify-between gap-1 px-1 py-1 text-xs! hover:bg-[#F3F1EC] hover:rounded-md",
      },
    },
    table: {
      slots: {
        th: "first:ps-0 last:pe-0",
        tr: "border-b-0",
        td: "whitespace-normal first:ps-0 last:pe-0 py-sm text-[18px]",
      },
    },
    textarea: {
      slots: {
        root: "w-full",
        base: "border border-0.5 border-neutral-100 font-medium placeholder:text-[#00103599] bg-white focus:ring-1! focus:ring-non! focus-visible:ring-non!",
      },
      defaultVariants: {
        size: "xl",
      },
    },
  },

  datoCmsPreview: {
    generatePreviewUrl: generatePreviewUrl,
  },
});

function generatePreviewUrl({ item, itemType, locale }: WebhookPayload, _: H3Event) {
  const localePrefix = locale.toLowerCase();
  const siteLocale = getDatoLocaleFromCulture(localePrefix);
  const slug = (item.attributes.slug && item.attributes.slug[locale]) || "";

  switch (itemType.attributes.api_key) {
    case "legal_page":
      return `/${localePrefix}${getSlugPrefix("legal", siteLocale)}/${slug}`;
    case "event":
      return `/${localePrefix}${getSlugPrefix("events", siteLocale)}/${slug}`;
    case "news":
      return `/${localePrefix}${getSlugPrefix("news", siteLocale)}/${slug}`;
    case "trends_inspiration":
      return `/${localePrefix}${getSlugPrefix("trends", siteLocale)}/${slug}`;
    case "store":
      return `/${localePrefix}${getSlugPrefix("stores", siteLocale)}/${slug}`;
    case "page":
      return `/${localePrefix}/${slug}`;
    case "layout":
      return `/${localePrefix}/`;
    default:
      return null;
  }
}
